import { Stack, Typography } from '@chronosphereio/chrono-ui';
import { useEffect, useState } from 'react';

type AIEditThinkingStateProps = {
  /**
   * A list of messages to show while the AI is "thinking".
   */
  messages?: readonly string[];
  /**
   * The maximum amount of time to show a message, in seconds.
   * Note: The component will stay on the last message.
   */
  maxDuration?: number;
};

const DEFAULT_MESSAGE = 'Thinking...';

export function AIEditThinkingState({ messages = [DEFAULT_MESSAGE], maxDuration = 3 }: AIEditThinkingStateProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentMessage = messages[currentIndex] ?? DEFAULT_MESSAGE;

  useEffect(() => {
    if (messages.length <= 1) return;
    if (currentIndex === messages.length - 1) return;

    const duration = getRandomDuration(0.5, maxDuration);
    const id = window.setTimeout(() => setCurrentIndex((i) => i + 1), duration);

    return () => window.clearTimeout(id);
  }, [messages, currentIndex, maxDuration]);

  return (
    <Stack py={0.5} color="text.legacySecondary">
      <Typography>
        {currentMessage.split('').map((char, i) => (
          <span
            key={i}
            style={{
              animation: 'wave 2s ease-in-out infinite',
              animationDelay: `${i * 0.05}s`,
            }}
          >
            {char}
          </span>
        ))}
      </Typography>
      <style>
        {`
        @keyframes wave {
          0%, 75%, 100% { opacity: 0.33; }
          50% { opacity: 1; }
        }
      `}
      </style>
    </Stack>
  );
}

function getRandomDuration(minSeconds = 0.5, maxSeconds = 3): number {
  const minMs = Math.max(0, minSeconds * 1000);
  const maxMs = Math.max(minMs, maxSeconds * 1000);
  return Math.floor(minMs + (maxMs - minMs) * Math.random());
}
